import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ContextProps } from '../types/ContextProps';
import { SaveButtonStatus, useSaveButtonStatus } from 'src/hooks/useSaveButtonStatus';
import { useToast } from 'src/app/useToast';
import { useStowagePlanStore } from './StowagePlanContext';

interface SaveRejectionRecovery {
    whenApplied: Promise<void>;
    onRejected: (activePlanGuid: string | undefined) => void;
}

type StowageSaveStatusContextType = {
    status: SaveButtonStatus;
    beginSave: (successMessage: string, failureMessage?: string, recovery?: SaveRejectionRecovery) => string;
    reset: () => void;
};

export const StowageSaveStatusContext = React.createContext<StowageSaveStatusContextType>(undefined!);

/**
 * Shared save status for the stowage pop-up panels.
 *
 * The panels close as soon as their save is dispatched, which would unmount a panel-local
 * useSaveButtonStatus (and its service worker listener) long before BACKGROUND_SAVE_COMPLETED
 * arrives. Keeping a single instance here — above every panel mount point — lets the confirmation
 * still be observed and surfaced as a toast after the panel is gone.
 *
 * Active saves are tracked in a map keyed by saveId so that overlapping or queued saves
 * (e.g. user opens and saves two modals in rapid succession) each reliably deliver their
 * respective success toast.
 */
export const StowageSaveStatusProvider = ({ children }: ContextProps) => {
    const { status, markSaving, reset: resetSaveButton } = useSaveButtonStatus();
    const { toastSuccess, toastError } = useToast();
    const { activeStowagePlan } = useStowagePlanStore();
    const pendingSavesRef = useRef<Map<string, string>>(new Map());
    const failureMessagesRef = useRef<Map<string, string>>(new Map());
    const recoveriesRef = useRef<Map<string, SaveRejectionRecovery>>(new Map());
    const rejectedSavesRef = useRef<SaveRejectionRecovery[]>([]);
    const recoveryGenerationRef = useRef(0);
    const [rejectionVersion, setRejectionVersion] = useState(0);

    const beginSave = useCallback(
        (successMessage: string, failureMessage?: string, recovery?: SaveRejectionRecovery): string => {
            const saveId = markSaving();
            pendingSavesRef.current.set(saveId, successMessage);
            if (failureMessage) failureMessagesRef.current.set(saveId, failureMessage);
            if (recovery) recoveriesRef.current.set(saveId, recovery);
            return saveId;
        },
        [markSaving]
    );

    const reset = useCallback(() => {
        pendingSavesRef.current.clear();
        failureMessagesRef.current.clear();
        recoveriesRef.current.clear();
        rejectedSavesRef.current = [];
        recoveryGenerationRef.current++;
        resetSaveButton();
    }, [resetSaveButton]);

    useEffect(() => {
        for (const recovery of rejectedSavesRef.current.splice(0)) {
            recovery.onRejected(activeStowagePlan?.stowagePlanGuid);
        }
    }, [rejectionVersion, activeStowagePlan?.stowagePlanGuid]);

    useEffect(() => {
        const handleSwMessage = (event: MessageEvent) => {
            const { type, saveId, errorType } = (event.data ?? {}) as { type?: string; saveId?: string; errorType?: string };
            if (!saveId) return;

            if (type === 'BACKGROUND_SAVE_COMPLETED') {
                const message = pendingSavesRef.current.get(saveId);
                if (message) {
                    toastSuccess(message);
                    pendingSavesRef.current.delete(saveId);
                }
                failureMessagesRef.current.delete(saveId);
                recoveriesRef.current.delete(saveId);
            } else if (type === 'BACKGROUND_SAVE_FAILED') {
                const recovery = recoveriesRef.current.get(saveId);
                if (recovery && errorType === 'network') return;
                recoveriesRef.current.delete(saveId);
                if (recovery && errorType === 'http') {
                    const generation = recoveryGenerationRef.current;
                    void recovery.whenApplied.then(() => {
                        if (generation !== recoveryGenerationRef.current) return;
                        rejectedSavesRef.current.push(recovery);
                        setRejectionVersion((prev) => prev + 1);
                    });
                }
                const message = failureMessagesRef.current.get(saveId);
                if (message) toastError(message);
                failureMessagesRef.current.delete(saveId);
                pendingSavesRef.current.delete(saveId);
            }
        };

        const handleSaveAborted = (event: Event) => {
            const detail = (event as CustomEvent<{ saveId: string }>).detail;
            if (detail?.saveId) {
                pendingSavesRef.current.delete(detail.saveId);
                failureMessagesRef.current.delete(detail.saveId);
                recoveriesRef.current.delete(detail.saveId);
            }
        };

        window.addEventListener('save-aborted', handleSaveAborted);
        if (navigator.serviceWorker) {
            navigator.serviceWorker.addEventListener('message', handleSwMessage);
        }

        return () => {
            window.removeEventListener('save-aborted', handleSaveAborted);
            if (navigator.serviceWorker) {
                navigator.serviceWorker.removeEventListener('message', handleSwMessage);
            }
        };
    }, [toastSuccess, toastError]);

    const value = useMemo(() => ({ status, beginSave, reset }), [status, beginSave, reset]);

    return <StowageSaveStatusContext.Provider value={value}>{children}</StowageSaveStatusContext.Provider>;
};

export const useStowageSaveStatusStore = () => useContext(StowageSaveStatusContext);
